import {
  CalculateOutlined,
  LockOpenOutlined as MuiLockOpenOutlined,
  LockOutlined as MuiLockOutlined
} from '@mui/icons-material';
import { Grid, IconButton, InputAdornment } from '@mui/material';
import { styled as MuiStyled } from '@mui/material/styles';
import { useTheme } from '@mui/system';
import useLayoutTypeContext from 'framework/base/hooks/useLayoutTypeContext';
import AryMobileCommaSeparatorTextField from 'framework/mobile/AryMobileCommaSeparatorTextField';
import React, { forwardRef, useEffect, useState } from 'react';
import AryCommaSeparatorTextField from './AryCommaSeparatorTextField';

const LockOpenOutlined = MuiStyled(MuiLockOpenOutlined)`
  width: ${(props) =>
    props.layouttype === 'desktop' || props.layouttype === 'laptop'
      ? '1.5rem'
      : '1.25rem'};
  height: ${(props) =>
    props.layouttype === 'desktop' || props.layouttype === 'laptop'
      ? '1.5rem'
      : '1.25rem'};
  transition: transform 0.5s ease-in-out;

  ${(props) =>
    props.shake &&
    `
    animation: shake 0.5s ease-in-out infinite;
  `}

  @keyframes shake {
    0% { transform: scale(1) rotate(0); }
    25% { transform: scale(1.1) rotate(-10deg); }
    50% { transform: scale(1.1) rotate(10deg); }
    75% { transform: scale(1.1) rotate(-10deg); }
    100% { transform: scale(1) rotate(0); }
  }
`;

const LockOutlined = MuiStyled(MuiLockOutlined)`
  width: ${(props) =>
    props.layouttype === 'desktop' || props.layouttype === 'laptop'
      ? '1.5rem'
      : '1.25rem'};
  height: ${(props) =>
    props.layouttype === 'desktop' || props.layouttype === 'laptop'
      ? '1.5rem'
      : '1.25rem'};
  transition: transform 0.5s ease-in-out;

  ${(props) =>
    props.shake &&
    `
    animation: shake 0.5s ease-in-out infinite;
  `}

  @keyframes shake {
    0% { transform: scale(1) rotate(0); }
    25% { transform: scale(1.1) rotate(-10deg); }
    50% { transform: scale(1.1) rotate(10deg); }
    75% { transform: scale(1.1) rotate(-10deg); }
    100% { transform: scale(1) rotate(0); }
  }
`;

const AryLockableTextField = forwardRef((props, ref) => {
  const {
    isLockable,
    isLocked,
    children,
    onUnlockClick,
    onLockClick,
    shouldShake = false,
    isCommission,
    commissionComponent,
    editLockFieldChangeHandler,
    customAdornment,
    hideEditIcon,
    ...others
  } = props;

  const { label } = props;
  const { dir } = document.body;
  const theme = useTheme();
  const [showHelperText, setShowHelperText] = useState('');
  const layoutType = useLayoutTypeContext();

  const adornment = {
    endAdornment: (
      <InputAdornment position={dir === 'rtl' ? 'end' : 'start'}>
        {isLockable ? (
          <IconButton
            tabIndex={-1}
            onClick={() => {
              if (!isLocked) {
                onLockClick(label);
              } else {
                setShowHelperText(onUnlockClick(label));
              }
            }}
            sx={{
              mr: children
                ? layoutType === 'desktop'
                  ? '.5rem'
                  : layoutType === 'laptop' || layoutType === 'tablet'
                  ? '.25rem'
                  : '4px'
                : ''
            }}
          >
            {isLocked ? (
              <LockOutlined layouttype={layoutType} />
            ) : (
              <LockOpenOutlined
                layouttype={layoutType}
                shake={!isLocked && !showHelperText && shouldShake}
              />
            )}
          </IconButton>
        ) : others?.changecalculationmethod ? (
          <IconButton
            onClick={(event) => {
              editLockFieldChangeHandler(event.currentTarget.parentElement);
            }}
            sx={{
              color: theme.text.primary500.color,
              mr: children
                ? layoutType === 'desktop'
                  ? '.5rem'
                  : layoutType === 'laptop' || layoutType === 'tablet'
                  ? '.25rem'
                  : '4px'
                : ''
            }}
          >
            <CalculateOutlined />
          </IconButton>
        ) : isCommission ? (
          commissionComponent
        ) : null}
        {customAdornment && customAdornment}
      </InputAdornment>
    )
  };

  useEffect(() => {
    if (!isLocked) {
      setShowHelperText('');
    }
  }, [isLocked]);

  return (
    <Grid container wrap="nowrap">
      {!!children && (
        <Grid item mr="-1px">
          {children}
        </Grid>
      )}
      <Grid item flexGrow={others.fullWidth ? 1 : undefined}>
        {layoutType === 'desktop' ? (
          <AryCommaSeparatorTextField
            ref={ref}
            inputAdornment={!hideEditIcon && adornment}
            showHelperText={showHelperText}
            error={Boolean(showHelperText)}
            {...others}
          />
        ) : (
          <AryMobileCommaSeparatorTextField
            ref={ref}
            inputAdornment={!hideEditIcon && adornment}
            showHelperText={showHelperText}
            error={Boolean(showHelperText)}
            {...others}
          />
        )}
      </Grid>
    </Grid>
  );
});

export default AryLockableTextField;
